import {useLayoutEffect, useRef, useState} from 'react'

/**
 * Reads the access token from the URL hash fragment (`#token=...`) supplied by
 * the mobile WebView, then immediately strips it from the visible URL.
 *
 * - The hash fragment is never transmitted to the server, so the token never
 *   appears in server access logs.
 * - We remove it from the address bar / history right after reading it, and we
 *   never persist it (it lives only in React state here and in the page).
 *
 * `isResolved` distinguishes "not read yet" (first render, before the layout
 * effect) from "read, no token found" so the page does not flash an error.
 */
export const useAccountDeletionToken = () => {
    const [token, setToken] = useState<string | null>(null)
    const [isResolved, setIsResolved] = useState(false)
    const hasRead = useRef(false)

    useLayoutEffect(() => {
        if (hasRead.current) return
        hasRead.current = true

        const rawHash = window.location.hash.replace(/^#/, '')
        const tokenFromHash = new URLSearchParams(rawHash).get('token')?.trim() || null

        setToken(tokenFromHash)
        setIsResolved(true)

        // Strip the token from the visible URL and from the browser history.
        if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search)
        }
    }, [])

    return {token, isResolved}
}
